<template>
    <!-- One compact block: greeting, suggestions and (page-provided) composer
         sit close together instead of being spread across the viewport. -->
    <div class="flex flex-col px-6 py-6">
        <div class="flex flex-col items-center text-center">
            <AiChatRobot v-if="showRobot" :size="112" :gap-ms="3200" />

            <h2 class="mt-4 text-2xl font-semibold text-gray-800 sm:text-3xl">
                {{ greeting }}
            </h2>
            <p class="mt-2 max-w-xl text-sm text-gray-500">
                {{ t("vue.ai_chat_welcome_intro") }}
            </p>
        </div>

        <!-- Suggested prompts, right below the greeting. -->
        <div class="mx-auto mt-6 flex w-full flex-col">
            <p
                class="mb-3 text-center text-[11px] font-medium uppercase tracking-wide text-gray-400"
            >
                {{ t("vue.ai_chat_examples_title") }}
            </p>
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <button
                    v-for="example in examples"
                    :key="example.text"
                    type="button"
                    :disabled="disabled"
                    class="flex items-start gap-3 rounded-xl border px-4 py-3 text-left text-sm transition"
                    :class="
                        disabled
                            ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                            : 'cursor-pointer border-gray-200 bg-white text-gray-700 hover:border-[#FF421E]/40 hover:bg-[#FF421E]/5'
                    "
                    @click="$emit('select', example.text)"
                >
                    <component
                        :is="example.icon"
                        class="mt-0.5 h-5 w-5 shrink-0"
                        :class="disabled ? 'text-gray-400' : 'text-[#FF421E]'"
                    />
                    <span>{{ example.text }}</span>
                </button>
            </div>

            <template v-if="teamSkillsActive">
                <p
                    class="mt-6 mb-3 text-center text-[11px] font-medium uppercase tracking-wide text-gray-400"
                >
                    {{ t("vue.ai_skills") }}
                </p>
                <div class="flex flex-wrap justify-center gap-2">
                    <button
                        v-for="skill in recentSkills"
                        :key="skill.id"
                        type="button"
                        :disabled="disabled"
                        class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition"
                        :class="
                            disabled
                                ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                                : 'cursor-pointer border-[#FF421E]/30 bg-[#FF421E]/5 text-[#FF421E] hover:bg-[#FF421E]/10'
                        "
                        :title="skill.summary ?? undefined"
                        @click="
                            $emit('select-skill', {
                                id: skill.id,
                                name: skill.name,
                            })
                        "
                    >
                        <SparklesIcon class="h-3.5 w-3.5" />
                        {{ skill.name }}
                    </button>
                    <inertia-link
                        v-if="recentSkills.length === 0"
                        :href="route('ai-skills.index')"
                        class="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50"
                    >
                        <SparklesIcon class="h-3.5 w-3.5" />
                        {{ t("vue.ai_skill_create") }}
                    </inertia-link>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { Component } from "vue";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    SparklesIcon,
    GlobeAltIcon,
    ChartBarIcon,
    Squares2X2Icon,
    ArchiveBoxXMarkIcon,
    ShieldCheckIcon,
    PaperAirplaneIcon,
} from "@heroicons/vue/24/outline";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import type { IAiSkillListItem } from "@/types/AiChat/IAiSkill";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

withDefaults(
    defineProps<{
        showRobot?: boolean;
        recentSkills?: IAiSkillListItem[];
        disabled?: boolean;
    }>(),
    { showRobot: true, recentSkills: () => [], disabled: false },
);

defineEmits<{
    (event: "select", message: string): void;
    (event: "select-skill", skill: { id: number; name: string }): void;
}>();

const teamSkillsActive = computed(
    () => usePage<IRhizomPageProps>().props.inAppAiChatTeamSkillsActive,
);

const firstName = computed(() => {
    const name = usePage<IRhizomPageProps>().props.user?.name ?? "";
    return name.split(" ")[0] ?? "";
});

const greeting = computed(() =>
    firstName.value
        ? t("vue.ai_chat_greeting_named", { name: firstName.value })
        : t("vue.ai_chat_greeting"),
);

interface IExample {
    text: string;
    icon: Component;
}

const examples = computed((): IExample[] => [
    { text: t("vue.ai_chat_example_domains"), icon: GlobeAltIcon },
    { text: t("vue.ai_chat_example_reports"), icon: ChartBarIcon },
    { text: t("vue.ai_chat_example_dashboard"), icon: Squares2X2Icon },
    { text: t("vue.ai_chat_example_blacklist"), icon: ArchiveBoxXMarkIcon },
    { text: t("vue.ai_chat_example_policy"), icon: ShieldCheckIcon },
    { text: t("vue.ai_chat_example_senders"), icon: PaperAirplaneIcon },
]);
</script>
